import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import { MarketingFooter } from '../components/layout/MarketingFooter';
import { useAuth } from '../contexts/AuthContext';

type BillingCycle = 'monthly' | 'yearly';
type PlanTier = 'starter' | 'business' | 'enterprise';

const PRICES: Record<
  PlanTier,
  { monthlyDisplay: string; yearlyPerMonth: string; yearlyBilled: string }
> = {
  starter: {
    monthlyDisplay: '$25',
    yearlyPerMonth: '$20',
    yearlyBilled: '$240/year',
  },
  business: {
    monthlyDisplay: '$125',
    yearlyPerMonth: '$110',
    yearlyBilled: '$1,320/year',
  },
  enterprise: {
    monthlyDisplay: '$299',
    yearlyPerMonth: '$219',
    yearlyBilled: '$2,628/year',
  },
};

const COMPLIANCE_FRAMEWORKS: { flag: string; label: string }[] = [
  { flag: '🇪🇺', label: 'GDPR — European Union' },
  { flag: '🇬🇧', label: 'UK GDPR — United Kingdom' },
  { flag: '🇺🇸', label: 'CCPA / NIST — United States' },
  { flag: '🇳🇬', label: 'NDPR — Nigeria' },
  { flag: '🇿🇦', label: 'POPIA — South Africa' },
  { flag: '🇰🇪', label: 'Kenya DPA — Kenya' },
  { flag: '🇬🇭', label: 'Ghana DPA — Ghana' },
  { flag: '🇮🇳', label: 'PDPB — India' },
  { flag: '🇸🇬', label: 'PDPA — Singapore' },
  { flag: '🇧🇷', label: 'LGPD — Brazil' },
  { flag: '🇦🇺', label: 'Australian Privacy Act — Australia' },
  { flag: '🌍', label: 'ISO 27001 — All other countries' },
];

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: 'Do I need any technical knowledge to use ShieldSME?',
    a: 'None at all. ShieldSME is built specifically for business owners, not IT experts. Everything is explained in plain English and setup takes less than 5 minutes.',
  },
  {
    q: 'Where is ShieldSME available?',
    a: 'ShieldSME works for businesses in any country worldwide. Our threat intelligence adapts to your region automatically, and compliance reports are tailored to the data protection laws where your business operates.',
  },
  {
    q: 'Is my business data safe with ShieldSME?',
    a: 'Yes. We use bank-level encryption to protect all your data. We never sell your information to third parties — ever. Our own platform is secured to the same standards we protect your business with.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Absolutely. No long-term contracts and no cancellation fees. Cancel with one click from your Settings page. You keep access until the end of your billing period.',
  },
  {
    q: 'What happens after my free trial?',
    a: 'You will receive a reminder 3 days before your trial ends. If you choose not to continue, your account is automatically paused — no charges without your explicit consent.',
  },
  {
    q: 'Can I change my plan later?',
    a: 'Yes. Upgrade or downgrade at any time from your Settings page. Upgrades take effect immediately. Downgrades take effect at the next billing cycle.',
  },
  {
    q: 'Do you offer discounts for nonprofits or schools?',
    a: 'Yes. Contact us at support@shieldsme.com for information about our nonprofit and education pricing.',
  },
];

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function TableCell({
  children,
  align = 'center',
}: {
  children: React.ReactNode;
  align?: 'left' | 'center';
}) {
  return (
    <td
      className={`border-b border-border px-3 py-3 text-sm text-muted sm:px-4 ${
        align === 'left' ? 'text-left font-medium text-foreground' : 'text-center'
      }`}
    >
      {children}
    </td>
  );
}

function IncludedCheck() {
  return (
    <span className="inline-flex justify-center text-accent" role="img" aria-label="Included">
      <CheckIcon className="h-5 w-5" aria-hidden />
    </span>
  );
}

function NotIncluded() {
  return <span className="text-muted/60">—</span>;
}

function PricingFaqAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="divide-y divide-border rounded-2xl border border-border bg-surface/50">
      {FAQ_ITEMS.map((item, index) => {
        const open = openIndex === index;
        return (
          <div key={item.q} className="px-4 sm:px-6">
            <button
              type="button"
              className="flex w-full min-h-[52px] items-center justify-between gap-4 py-4 text-left text-base font-medium text-foreground transition hover:text-accent"
              aria-expanded={open}
              onClick={() => setOpenIndex(open ? null : index)}
            >
              <span>{item.q}</span>
              <span
                className={`shrink-0 text-accent transition-transform duration-300 ease-out ${
                  open ? 'rotate-180' : 'rotate-0'
                }`}
                aria-hidden
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              }`}
            >
              <div className="overflow-hidden">
                <p className="max-w-3xl pb-4 text-sm leading-relaxed text-muted sm:text-base">{item.a}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PricingPage() {
  const { currentUser, userProfile, loading, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  const isLoggedIn = Boolean(currentUser);
  const currentPlan: PlanTier = (userProfile?.subscription_tier ?? 'starter') as PlanTier;
  const hasUsedTrial = userProfile?.has_used_trial === true;
  const primaryCtaLabel =
    isLoggedIn && hasUsedTrial ? 'Upgrade Plan' : 'Start Free 14-Day Trial';

  const trialHref = isLoggedIn ? '/settings' : '/signup';
  const bottomCtaHref = trialHref;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  const starterFeatures = useMemo(
    () => [
      'Security Health Score (daily)',
      'Email threat monitoring (1 inbox)',
      'Dark web monitoring (1 domain)',
      'Weekly security brief by email',
      'Plain-English alert recommendations',
      'Global compliance baseline report',
      'Email support (48hr response)',
    ],
    []
  );

  const businessFeatures = useMemo(
    () => [
      'Everything in Starter',
      'Email monitoring (up to 10 inboxes)',
      'Dark web monitoring (3 domains)',
      'Instant WhatsApp alerts',
      'Supplier risk scoring (up to 5 suppliers)',
      'Country-specific compliance report (GDPR, NDPR, CCPA, PDPA, etc.)',
      'Monthly executive security report',
      'Priority support (12hr response)',
    ],
    []
  );

  const enterpriseFeatures = useMemo(
    () => [
      'Everything in Business',
      'Unlimited inboxes and domains',
      'Dedicated security analyst',
      'Incident response support (2hr SLA)',
      'Custom compliance reports for any jurisdiction',
      'Supplier risk scoring (unlimited)',
      'API access for internal integrations',
      'White-glove onboarding',
      'Phone support',
    ],
    []
  );

  return (
    <main className="overflow-x-hidden bg-navy text-foreground">
      <header
        className={`fixed top-0 z-50 w-full border-b transition-[background-color,backdrop-filter,border-color] duration-300 ${
          scrolled
            ? 'border-border/60 bg-navy/75 backdrop-blur-md'
            : 'border-transparent bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:h-[4.25rem] sm:px-6 lg:px-8">
          <div className="text-lg sm:text-xl">
            <Logo size="md" onNavigate={closeMobile} />
          </div>

          <nav className="hidden items-center gap-2 md:flex md:gap-3" aria-label="Primary">
            <ThemeToggle />
            {isLoggedIn ? (
              <>
                <Link
                  to="/dashboard"
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-foreground/5"
                >
                  Dashboard
                </Link>
                <button
                  type="button"
                  onClick={() => void signOut()}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-cta-text transition hover:bg-accent/90"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-foreground/5"
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-cta-text transition hover:bg-accent/90"
                >
                  Start Free Trial
                </Link>
              </>
            )}
          </nav>

          <div className="flex items-center gap-2 md:hidden">
            <ThemeToggle />
            <button
              type="button"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-border p-2 text-foreground"
              aria-expanded={mobileOpen}
              aria-controls="pricing-mobile-nav"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div
          id="pricing-mobile-nav"
          className={`md:hidden ${mobileOpen ? 'block' : 'hidden'} border-t border-border/60 bg-navy/95 backdrop-blur-md`}
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-4 sm:px-6">
            {isLoggedIn ? (
              <>
                <Link
                  to="/dashboard"
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-border px-4 py-3 text-center font-medium text-foreground hover:bg-foreground/5"
                  onClick={closeMobile}
                >
                  Dashboard
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    closeMobile();
                    void signOut();
                  }}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-4 py-3 text-center font-semibold text-cta-text hover:bg-accent/90"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-border px-4 py-3 text-center font-medium text-foreground hover:bg-foreground/5"
                  onClick={closeMobile}
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-4 py-3 text-center font-semibold text-cta-text hover:bg-accent/90"
                  onClick={closeMobile}
                >
                  Start Free Trial
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Page header */}
      <section className="relative px-4 pb-12 pt-28 sm:px-6 sm:pb-16 sm:pt-32 lg:px-8 lg:pt-36">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,color-mix(in_srgb,var(--accent-green)_12%,transparent),transparent)]" />
        <div className="relative mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">PRICING</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Straightforward Pricing for Every Business
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
            Protect your business for less than the cost of one business lunch per month. No hidden fees. Cancel
            anytime.
          </p>

          <div className="mx-auto mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <div
              className="flex w-full max-w-md justify-center rounded-xl border border-border bg-surface/80 p-1"
              role="group"
              aria-label="Billing period"
            >
              <button
                type="button"
                onClick={() => setBillingCycle('monthly')}
                className={`min-h-[44px] flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  billingCycle === 'monthly'
                    ? 'bg-accent text-cta-text shadow-sm'
                    : 'text-muted hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle('yearly')}
                className={`relative min-h-[44px] flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  billingCycle === 'yearly'
                    ? 'bg-accent text-cta-text shadow-sm'
                    : 'text-muted hover:text-foreground'
                }`}
              >
                Yearly
              </button>
            </div>
            <span
              className={`inline-flex items-center rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent transition-all duration-300 ${
                billingCycle === 'yearly' ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-1 opacity-0'
              }`}
            >
              Save 20%
            </span>
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="border-t border-border/80 px-4 pb-16 sm:px-6 sm:pb-20 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-3 lg:gap-6">
          {/* Starter */}
          <div className="relative flex h-full flex-col rounded-2xl border border-border bg-surface p-8 pt-10">
            <div className="min-h-[28px]">
              {!loading && isLoggedIn && currentPlan === 'starter' ? (
                <span className="inline-flex w-fit rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
                  Your current plan
                </span>
              ) : null}
            </div>
            <h2 className="mt-2 text-xl font-bold text-foreground">Starter</h2>
            <p className="mt-2 text-sm text-muted">For solo business owners and micro businesses</p>
            <div className="mt-6 min-h-[5.5rem]">
              <p className="text-4xl font-bold text-foreground tabular-nums">
                <span key={`starter-${billingCycle}`} className="inline-block animate-fade-in-up">
                  {billingCycle === 'monthly'
                    ? PRICES.starter.monthlyDisplay
                    : PRICES.starter.yearlyPerMonth}
                </span>
                <span className="text-lg font-normal text-muted">/month</span>
              </p>
              <p
                className={`mt-2 text-sm transition-opacity duration-300 ease-out ${
                  billingCycle === 'yearly' ? 'text-muted opacity-100' : 'opacity-0'
                }`}
              >
                Billed as {PRICES.starter.yearlyBilled}
              </p>
            </div>
            <ul className="mt-6 flex flex-1 flex-col gap-3 text-sm text-muted">
              {starterFeatures.map((f) => (
                <li key={f} className="flex gap-2">
                  <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to={trialHref}
              className="mt-8 inline-flex min-h-[48px] w-full items-center justify-center rounded-lg border border-border px-4 py-3 text-center text-base font-semibold text-foreground transition hover:bg-foreground/5"
            >
              {primaryCtaLabel}
            </Link>
          </div>

          {/* Business — featured */}
          <div className="relative flex h-full flex-col rounded-2xl border-2 border-accent bg-surface p-8 pt-10 shadow-accent-popular">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent px-3 py-0.5 text-xs font-semibold text-cta-text">
              Most Popular
            </span>
            <div className="min-h-[28px]">
              {!loading && isLoggedIn && currentPlan === 'business' ? (
                <span className="inline-flex w-fit rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
                  Your current plan
                </span>
              ) : null}
            </div>
            <h2 className="mt-2 text-xl font-bold text-foreground">Business</h2>
            <p className="mt-2 text-sm text-muted">For growing businesses with a small team</p>
            <div className="mt-6 min-h-[5.5rem]">
              <p className="text-4xl font-bold text-foreground tabular-nums">
                <span key={`business-${billingCycle}`} className="inline-block animate-fade-in-up">
                  {billingCycle === 'monthly'
                    ? PRICES.business.monthlyDisplay
                    : PRICES.business.yearlyPerMonth}
                </span>
                <span className="text-lg font-normal text-muted">/month</span>
              </p>
              <p
                className={`mt-2 text-sm transition-opacity duration-300 ease-out ${
                  billingCycle === 'yearly' ? 'text-muted opacity-100' : 'opacity-0'
                }`}
              >
                Billed as {PRICES.business.yearlyBilled}
              </p>
            </div>
            <ul className="mt-6 flex flex-1 flex-col gap-3 text-sm text-muted">
              {businessFeatures.map((f) => (
                <li key={f} className="flex gap-2">
                  <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to={trialHref}
              className="mt-8 inline-flex min-h-[48px] w-full items-center justify-center rounded-lg bg-accent px-4 py-3 text-center text-base font-semibold text-cta-text transition hover:bg-accent/90"
            >
              {primaryCtaLabel}
            </Link>
          </div>

          {/* Enterprise */}
          <div className="relative flex h-full flex-col rounded-2xl border border-border bg-surface p-8 pt-10">
            <div className="min-h-[28px]">
              {!loading && isLoggedIn && currentPlan === 'enterprise' ? (
                <span className="inline-flex w-fit rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
                  Your current plan
                </span>
              ) : null}
            </div>
            <h2 className="mt-2 text-xl font-bold text-foreground">Enterprise</h2>
            <p className="mt-2 text-sm text-muted">For established businesses needing full protection</p>
            <div className="mt-6 min-h-[5.5rem]">
              <p className="text-4xl font-bold text-foreground tabular-nums">
                <span key={`enterprise-${billingCycle}`} className="inline-block animate-fade-in-up">
                  {billingCycle === 'monthly'
                    ? PRICES.enterprise.monthlyDisplay
                    : PRICES.enterprise.yearlyPerMonth}
                </span>
                <span className="text-lg font-normal text-muted">/month</span>
              </p>
              <p
                className={`mt-2 text-sm transition-opacity duration-300 ease-out ${
                  billingCycle === 'yearly' ? 'text-muted opacity-100' : 'opacity-0'
                }`}
              >
                Billed as {PRICES.enterprise.yearlyBilled}
              </p>
            </div>
            <ul className="mt-6 flex flex-1 flex-col gap-3 text-sm text-muted">
              {enterpriseFeatures.map((f) => (
                <li key={f} className="flex gap-2">
                  <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <a
              href="mailto:sales@lognsec.com"
              className="mt-8 inline-flex min-h-[48px] w-full items-center justify-center rounded-lg border border-border px-4 py-3 text-center text-base font-semibold text-foreground transition hover:bg-foreground/5"
            >
              Contact Sales
            </a>
          </div>
        </div>
      </section>

      {/* Feature comparison */}
      <section className="border-t border-border/80 bg-section-dim px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Compare plans
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-sm text-muted sm:text-base">
            Full feature matrix across Starter, Business, and Enterprise.
          </p>
          <div className="mt-10 overflow-x-auto rounded-2xl border border-border bg-surface/80 shadow-sm">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="border-b border-border bg-surface">
                  <th scope="col" className="px-3 py-4 text-sm font-semibold text-foreground sm:px-4">
                    Feature
                  </th>
                  <th scope="col" className="px-3 py-4 text-center text-sm font-semibold text-foreground sm:px-4">
                    Starter
                  </th>
                  <th scope="col" className="px-3 py-4 text-center text-sm font-semibold text-accent sm:px-4">
                    Business
                  </th>
                  <th scope="col" className="px-3 py-4 text-center text-sm font-semibold text-foreground sm:px-4">
                    Enterprise
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <TableCell align="left">Security Health Score</TableCell>
                  <TableCell>Daily</TableCell>
                  <TableCell>Daily</TableCell>
                  <TableCell>Daily</TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Email inboxes monitored</TableCell>
                  <TableCell>1 inbox</TableCell>
                  <TableCell>10 inboxes</TableCell>
                  <TableCell>Unlimited</TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Domains monitored</TableCell>
                  <TableCell>1</TableCell>
                  <TableCell>3</TableCell>
                  <TableCell>Unlimited</TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Dark web monitoring</TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                </tr>
                <tr>
                  <TableCell align="left">WhatsApp alerts</TableCell>
                  <TableCell>
                    <NotIncluded />
                  </TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Supplier risk scoring</TableCell>
                  <TableCell>
                    <NotIncluded />
                  </TableCell>
                  <TableCell>Up to 5 suppliers</TableCell>
                  <TableCell>Unlimited</TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Weekly security brief</TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Compliance report type</TableCell>
                  <TableCell>Global baseline</TableCell>
                  <TableCell>Country-specific</TableCell>
                  <TableCell>Custom (any jurisdiction)</TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Monthly executive report</TableCell>
                  <TableCell>
                    <NotIncluded />
                  </TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Incident response SLA</TableCell>
                  <TableCell>
                    <NotIncluded />
                  </TableCell>
                  <TableCell>
                    <NotIncluded />
                  </TableCell>
                  <TableCell>2hr</TableCell>
                </tr>
                <tr>
                  <TableCell align="left">API access</TableCell>
                  <TableCell>
                    <NotIncluded />
                  </TableCell>
                  <TableCell>
                    <NotIncluded />
                  </TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Dedicated analyst</TableCell>
                  <TableCell>
                    <NotIncluded />
                  </TableCell>
                  <TableCell>
                    <NotIncluded />
                  </TableCell>
                  <TableCell>
                    <IncludedCheck />
                  </TableCell>
                </tr>
                <tr>
                  <TableCell align="left">Support response time</TableCell>
                  <TableCell>48hr (email)</TableCell>
                  <TableCell>12hr (priority)</TableCell>
                  <TableCell>Phone</TableCell>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Global compliance callout */}
      <section className="border-t border-border/80 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-2xl border border-border bg-surface p-8 sm:p-10 lg:p-12">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-12">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-accent/30 bg-accent/10 text-accent">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.75}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                  Compliance Built for Your Country
                </h2>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted sm:text-base">
                  Whether your business operates under GDPR in Europe, NDPR in Nigeria, CCPA in California, POPIA in
                  South Africa, or any other framework — ShieldSME automatically generates compliance reports relevant to
                  where your business is based.
                </p>
                <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {COMPLIANCE_FRAMEWORKS.map((row) => (
                    <li
                      key={row.label}
                      className="flex items-start gap-2 rounded-lg border border-border/80 bg-navy/40 px-3 py-2.5 text-sm text-foreground"
                    >
                      <span className="text-lg leading-none" aria-hidden>
                        {row.flag}
                      </span>
                      <span>{row.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border/80 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-accent">FAQ</p>
          <h2 className="mt-3 text-center text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Common questions
          </h2>
          <div className="mt-10">
            <PricingFaqAccordion />
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-border/80 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl border-2 border-accent bg-footer-deep px-6 py-12 text-center shadow-lg sm:px-10 sm:py-14">
            <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Your Business Deserves Protection
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-muted sm:text-lg">
              Start your free 14-day trial today. No credit card. No commitment. Cancel anytime.
            </p>
            <Link
              to={bottomCtaHref}
              className="mt-8 inline-flex min-h-[48px] items-center justify-center rounded-lg bg-accent px-6 py-3 text-base font-semibold text-cta-text transition hover:bg-accent/90"
            >
              {isLoggedIn && hasUsedTrial ? 'Upgrade now' : 'Get Started Free'}
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </main>
  );
}
